import { ValidationError } from '@app/domain/entities';

export type TableCell = string | number | Date | boolean | null | undefined;

export function isSupportedStatementFile(file: File): boolean {
  return isCsvFile(file) || isExcelFile(file);
}

export function isCsvFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith('.csv') || file.type === 'text/csv' || file.type === 'application/csv';
}

export function isExcelFile(file: File): boolean {
  const name = file.name.toLowerCase();
  return name.endsWith('.xlsx') || file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet';
}

export async function parseCsvFile(file: File): Promise<TableCell[][]> {
  const text = await file.text();
  return parseCsvText(text);
}

export function parseCsvText(text: string): TableCell[][] {
  const lines = text
    .replace(/\r\n/g, '\n')
    .replace(/\r/g, '\n')
    .split('\n')
    .filter((line) => line.trim().length > 0);

  if (lines.length === 0) {
    return [];
  }

  const delimiter = detectDelimiter(lines[0]);
  return lines.map((line) => splitCsvLine(line, delimiter));
}

function detectDelimiter(headerLine: string): string {
  const counts = {
    ';': (headerLine.match(/;/g) ?? []).length,
    ',': (headerLine.match(/,/g) ?? []).length,
    '\t': (headerLine.match(/\t/g) ?? []).length,
  };

  if (counts[';'] >= counts[','] && counts[';'] >= counts['\t']) return ';';
  if (counts['\t'] >= counts[',']) return '\t';
  return ',';
}

function splitCsvLine(line: string, delimiter: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const ch = line[i];

    if (ch === '"') {
      if (inQuotes && line[i + 1] === '"') {
        current += '"';
        i++;
      } else {
        inQuotes = !inQuotes;
      }
    } else if (ch === delimiter && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }

  result.push(current.trim());
  return result;
}

// ExcelJS is lazy-loaded to keep it out of the initial app bundle.
// Cached so that subsequent calls within the same session skip the dynamic import overhead.
let excelJsPromise: Promise<typeof import('exceljs')> | null = null;
function loadExcelJs(): Promise<typeof import('exceljs')> {
  return (excelJsPromise ??= import('exceljs').then((m) => (m.default ?? m) as typeof import('exceljs')));
}

export async function parseExcelFile(file: File): Promise<TableCell[][]> {
  const buffer = await file.arrayBuffer();
  const ExcelJS = await loadExcelJs();
  const workbook = new ExcelJS.Workbook();

  try {
    await workbook.xlsx.load(buffer);
  } catch (error) {
    console.warn('Failed to read the Excel file', error);
    throw new ValidationError(
      'file',
      'Failed to read the Excel file. Make sure it is a valid .xlsx file, or export your statement as CSV instead.',
    );
  }

  const worksheet = workbook.worksheets[0];
  if (!worksheet) {
    throw new ValidationError('file', 'The Excel file does not contain any worksheets');
  }

  const rows: TableCell[][] = [];
  worksheet.eachRow((row) => {
    // row.values is 1-indexed; index 0 is always undefined
    const values = row.values as unknown[];
    const cells: TableCell[] = [];
    for (let i = 1; i < values.length; i++) {
      cells.push(resolveExcelJsCell(values[i]));
    }
    rows.push(cells);
  });

  return rows;
}

function resolveExcelJsCell(value: unknown): TableCell {
  if (value === null || value === undefined) return null;
  if (value instanceof Date) return value;
  if (typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') return value;
  if (typeof value === 'object') {
    const v = value as Record<string, unknown>;
    if ('richText' in v) {
      return (v['richText'] as { text: string }[]).map((rt) => rt.text).join('');
    }
    if ('result' in v) {
      // Formula cell — use the cached result value
      const result = v['result'];
      if (result instanceof Date) return result;
      if (typeof result === 'string' || typeof result === 'number' || typeof result === 'boolean') return result;
      return null;
    }
    if ('text' in v) return String(v['text'] || '');
    if ('error' in v) return null;
  }
  return null;
}
